import { useState, useEffect, useRef } from "react";
import { useAction, useQuery } from "convex/react";
import { useUser, useClerk } from "@clerk/react";
import { api } from "../lib/convexApi";
import { openHostedCheckout } from "../billing/hostedCheckout";
import { parseGetParams } from "../funnel/getParams";
import type { Tier, BillingPeriod } from "../funnel/getParams";
import { setDownloadIntent } from "../funnel/intent";
import { AuthShell } from "./AuthShell";
import { CheckoutLaunch, type LaunchPhase } from "./CheckoutLaunch";

const ENTERPRISE_URL = "https://steerly.ai/contact-enterprise";

// How many DLP patterns ship (every tier), counted at build time by
// vite.config.ts. Only the number is injected, so the card can't go stale
// when a pattern is added and the regexes stay out of the client bundle.
const DLP_PATTERN_COUNT = import.meta.env.VITE_DLP_PATTERN_COUNT;

// Annual is billed yearly at a 20% discount; `annual` is the effective
// per-seat/mo price shown on the card, `annualBilled` is the actual yearly
// charge. Prices mirror the live Lemon Squeezy variants exactly.
// (Base: $20 → $16/mo = $192/yr · Pro: $50 → $40/mo = $480/yr ·
// Ultra Security: $100 → $80/mo = $960/yr.)
export const PLANS: Array<{
  tier: Tier;
  name: string;
  monthly: string;
  annual: string;
  annualBilled: string;
  features: string[];
}> = [
  {
    tier: "base",
    name: "Base",
    monthly: "$20",
    annual: "$16",
    annualBilled: "$192/yr",
    features: [
      "Multi-agent workspace",
      "Persistent terminals",
      "File editor",
      "Unlimited sessions",
    ],
  },
  {
    tier: "pro",
    name: "Pro",
    monthly: "$50",
    annual: "$40",
    annualBilled: "$480/yr",
    features: [
      "Everything in Base",
      "PR risk briefs (0-100)",
      "Auto-remediation review loop",
      "GitHub App + status checks",
      "Audit export",
    ],
  },
  {
    tier: "security",
    name: "Ultra Security",
    monthly: "$100",
    annual: "$80",
    annualBilled: "$960/yr",
    features: [
      "Everything in Pro",
      "Command firewall - allow / ask / deny",
      "Policy engine + 10 packs",
      `DLP (${DLP_PATTERN_COUNT} patterns)`,
      "Security Room",
      "Anomaly detection · memory graph",
    ],
  },
];

export function PricingPage() {
  // Account control for the gate. A signed-in-but-unentitled user can land
  // here (expired session, a workspace with no subscription, a dev backend
  // mid-state). Without an escape hatch the gate is a trap - surface the
  // current account and let them sign out to switch / re-authenticate.
  const { user } = useUser();
  const clerk = useClerk();
  const accountEmail =
    user?.primaryEmailAddress?.emailAddress ?? user?.emailAddresses?.[0]?.emailAddress ?? null;

  const createCheckout = useAction(api.billing.checkout.createCheckout);
  // Only offer Annual when the store actually has yearly variants configured.
  // Keep the raw value (undefined = loading) so the auto-open effect can
  // distinguish "still loading" from "definitively false" for yearly URLs.
  const yearlyRaw = useQuery(api.billing.queries.yearlyAvailable) as boolean | undefined;
  // `=== true` collapses both the loading (undefined) and false cases to "monthly only"
  // so we never flash an Annual toggle that would hit a createCheckout throwing on a
  // missing LEMON_SQUEEZY_VARIANT_*_YEARLY env (Codex).
  const yearlyAvailable = yearlyRaw === true;
  const [busy, setBusy] = useState<Tier | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [period, setPeriod] = useState<BillingPeriod>("monthly");
  // Defensive: if the toggle was somehow on yearly when availability flips off,
  // never send period:"yearly" to a store that can't fulfill it.
  const effectivePeriod: BillingPeriod = yearlyAvailable ? period : "monthly";

  // Funnel deep-link: a /get?plan=…&billing=… arrival means the visitor ALREADY
  // chose a plan on the marketing site. We render the focused CheckoutLaunch
  // screen (not the full grid) and auto-open the checkout - unless they ask to
  // see every plan. `deepLink` is parsed once via a lazy state initializer (the
  // URL never changes mid-mount); a bare useRef(parse(...)) would re-run the
  // parse on every render and throw the result away.
  const [deepLink] = useState(() => parseGetParams(window.location.search));
  const launchPlan = deepLink.plan ? PLANS.find((p) => p.tier === deepLink.plan) : undefined;
  const [showAllPlans, setShowAllPlans] = useState(!launchPlan);
  // Starts "ready", not "opening": when the auto-open is suppressed (the
  // once-per-session flag is set - e.g. the buyer pressed Back from the LS
  // page), the launch screen must offer an enabled "Open checkout" button,
  // not an eternal spinner. The auto-open path flips to "opening" itself.
  const [launchPhase, setLaunchPhase] = useState<LaunchPhase>("ready");
  // "See all plans" must really mean it: the auto-opened createCheckout call
  // may still be in flight when the user bails to the grid, and without this
  // guard its .then() would fire openHostedCheckout a beat later and yank the
  // user to LS anyway (the grid flashes, then the redirect wins). A ref - not
  // state - because the in-flight closure must see the CURRENT value at
  // resolve time, not the value captured at call time.
  const abortAutoRedirect = useRef(false);

  async function subscribeWithPeriod(
    tier: Tier,
    p: BillingPeriod,
    opts?: { fromAutoOpen?: boolean }
  ) {
    setBusy(tier);
    setNotice(null);
    setLaunchPhase("opening");
    try {
      const res = await createCheckout({ tier, period: p });
      if (res.ok) {
        if (opts?.fromAutoOpen && abortAutoRedirect.current) {
          // The user clicked "See all plans" while this auto-open was being
          // prepared - they chose the grid; don't redirect out from under it.
          return;
        }
        setDownloadIntent();
        openHostedCheckout(res.url);
        setLaunchPhase("opened");
      } else if (res.reason === "already-subscribed") {
        setNotice(
          "You already have an active subscription. Manage it from Admin → Plan & billing."
        );
        setLaunchPhase("subscribed");
      }
    } catch {
      setNotice("Could not start checkout. Please try again.");
      setLaunchPhase("error");
    } finally {
      setBusy(null);
    }
  }

  // Auto-open the checkout once when the component mounts via a /get deep-link.
  // If the URL requests yearly billing, we wait until yearlyRaw resolves from
  // undefined (loading) so we never open a monthly checkout when annual was
  // requested and is actually available.
  //
  // Once per TAB SESSION, not per mount (sessionStorage): the redirect is a
  // full-page navigation now, so a buyer who abandons the LS page and presses
  // Back lands on this component again with the same /get URL - a per-mount
  // ref would re-fire the redirect and trap the Back button in a loop. With
  // the session flag they land on the launch screen, where "Open checkout"
  // is one click away.
  const AUTO_OPEN_KEY = "steerly:get-auto-opened";
  const autoOpened = useRef(false);
  useEffect(() => {
    if (autoOpened.current) return;
    const { plan, period: urlPeriod } = parseGetParams(window.location.search);
    if (!plan) return;
    // If the URL requests yearly, wait until availability resolves (undefined = loading).
    if (urlPeriod === "yearly" && yearlyRaw === undefined) return;
    autoOpened.current = true;
    try {
      if (sessionStorage.getItem(AUTO_OPEN_KEY) === "1") return;
      sessionStorage.setItem(AUTO_OPEN_KEY, "1");
    } catch {
      /* sessionStorage unavailable → fall back to per-mount behavior */
    }
    const resolved: BillingPeriod =
      urlPeriod === "yearly" && yearlyRaw === true ? "yearly" : "monthly";
    setPeriod(resolved);
    void subscribeWithPeriod(plan, resolved, { fromAutoOpen: true });
    // subscribeWithPeriod closes only over stable refs (useAction + useState setters); deps intentionally [yearlyRaw].
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yearlyRaw]);

  // Deep-link funnel: skip the redundant plan grid and drop straight into the
  // checkout-launch screen for the plan the visitor already picked.
  if (launchPlan && !showAllPlans) {
    const resolvedPeriod: BillingPeriod =
      deepLink.period === "yearly" && yearlyAvailable ? "yearly" : "monthly";
    return (
      <CheckoutLaunch
        planName={launchPlan.name}
        periodLabel={resolvedPeriod === "yearly" ? "Annual" : "Monthly"}
        priceLabel={resolvedPeriod === "yearly" ? launchPlan.annual : launchPlan.monthly}
        phase={launchPhase}
        onOpenCheckout={() => void subscribeWithPeriod(launchPlan.tier, resolvedPeriod)}
        onSeeAllPlans={() => {
          abortAutoRedirect.current = true;
          setShowAllPlans(true);
        }}
        accountEmail={accountEmail}
        onSignOut={() => void clerk.signOut()}
      />
    );
  }

  return (
    <AuthShell wide>
      <div className="pricing-page">
        <div className="pricing-page__account">
          <div className="pricing-page__account-left">
            {accountEmail ? (
              <>
                <span className="pricing-page__account-dot" aria-hidden="true" />
                <span className="pricing-page__account-label">Signed in as</span>
                <span className="pricing-page__account-email">{accountEmail}</span>
              </>
            ) : (
              <span className="pricing-page__account-label">Not signed in</span>
            )}
          </div>
          <div className="pricing-page__account-actions">
            {accountEmail ? (
              <button
                type="button"
                className="pricing-page__account-btn"
                onClick={() => void clerk.signOut()}
              >
                Sign out / switch account
              </button>
            ) : (
              <button
                type="button"
                className="pricing-page__account-btn is-primary"
                onClick={() => clerk.openSignIn?.({})}
              >
                Sign in
              </button>
            )}
          </div>
        </div>

        <header className="pricing-page__header">
          <h1>Choose your plan</h1>
          <p>Per-seat pricing. Pick a plan to start using Steerly.</p>
        </header>

        {yearlyAvailable && (
          <div className="pricing-toggle" role="group" aria-label="Billing period">
            <button
              type="button"
              className="pricing-toggle__option"
              aria-pressed={period === "monthly"}
              onClick={() => setPeriod("monthly")}
            >
              Monthly
            </button>
            <button
              type="button"
              className="pricing-toggle__option"
              aria-pressed={period === "yearly"}
              onClick={() => setPeriod("yearly")}
            >
              Annual
              <span className="pricing-toggle__save">Save 20%</span>
            </button>
          </div>
        )}

        <section className="pricing-page__plans">
          {PLANS.map((p) => {
            const price = effectivePeriod === "yearly" ? p.annual : p.monthly;
            return (
              <article key={p.tier} className="pricing-card">
                <div className="pricing-card__header">
                  <h2 className="pricing-card__title">{p.name}</h2>
                  <span className="pricing-card__price">
                    {price}
                    <span className="pricing-card__per">/seat/mo</span>
                  </span>
                  {effectivePeriod === "yearly" && (
                    <span className="pricing-card__billed">billed {p.annualBilled}</span>
                  )}
                </div>
                <ul className="pricing-card__features">
                  {p.features.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="pricing-card__cta"
                  disabled={busy !== null}
                  onClick={() => subscribeWithPeriod(p.tier, effectivePeriod)}
                >
                  {busy === p.tier ? "Taking you to checkout…" : `Subscribe to ${p.name}`}
                </button>
              </article>
            );
          })}
        </section>

        {notice && (
          <p className="pricing-page__notice" role="status">
            {notice}
          </p>
        )}

        <section className="pricing-page__enterprise">
          <article className="enterprise-card">
            <h2 className="enterprise-card__title">Enterprise</h2>
            <p className="enterprise-card__desc">
              For teams, regulated industries, and on-prem deployments - SSO, custom policy packs,
              audit retention, and SLAs.
            </p>
            <a
              className="enterprise-card__cta"
              href={ENTERPRISE_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Contact us
            </a>
          </article>
        </section>
      </div>
    </AuthShell>
  );
}
